import { LangUtil } from '../../../../../common/utils/lang-util';
import { Navigator } from '../../../../effects/navigator';
import { checkUserLanguages, ensureAnonymousSession } from '../../../../effects/prepareAfterOpen';
import { CallActions } from '../../../../store/call/actions';
import { CallSelectors } from '../../../../store/call/selectors';
import { ICallState } from '../../../../store/call/types';
import { getAuthUser, getAuthUserLangCode } from '../../../../store/general/selectors';
import { getConferenceUserGroups, getTargetLanguage, getUserGroupById, isUserGroupsLoaded } from '../../../../store/models/selectors';
import { AppThunk } from '../../../../store/types';
import { ConferenceRest, TConferenceCreateRoomResult, TConferenceJoinResult, TConferenceSaveLevel } from '../../../../../common/rest/conference/conferenceRest';
import { TAudio, TAudioType, TConferenceProgress, TUserGroup, TUserLanguage } from '../../../../store/models/types';
import { addAudioInGroup, addGroup, refreshCallGroupAction, removeAudioFromGroupAction, removeGroupAction, updateAudioAction } from '../../../../store/models/actions';
import { AudioRest } from '../../../../../common/rest/audio/audioRest';
import { buildProgressPatch, mergeProgressLog } from '../../../../../common/utils/conferenceProgress';
import { getDispatch, getState } from '../../../../store';
import { EventsRouter } from '../../../../../common/events/eventsRouter';
import { AuthUserEffects } from '../../../../effects/authUserEffects';
import { Events } from '../../../../../common/events/types';
import { EUserGroupType, EUserLevel } from '../../../../store/general/types';
import { UrlUtil } from '../../../../../common/utils/url-util';
import { CallLangs } from '../../langs';
import { CallAppEffects } from '../../hocs/events';
import { updateUserGroups } from '../../../../effects/updateUserGroups';
import { trackRoomCreated } from '../../../../../common/analytics-addon';

// Server's call start in this client's clock; skew error is ~half the request's round trip.
const toClientStartedAt = (startedAt?: number, serverNow?: number): number | undefined =>
	startedAt && serverNow ? startedAt + (Date.now() - serverNow) : undefined;

export class CallAppEvents {

	public static onLoad(
	): AppThunk {
		return async (
			dispatch,
			getState
		) => {
			if (!UrlUtil.needsAuthBootstrap()) return;
			if (!getAuthUser(getState())) { // url
				try {
					let nativeLangCode = LangUtil.checkLangCode(navigator.language);
					let targetLangCode = getTargetLanguage(getState())?.code || 'en';
					if (nativeLangCode === targetLangCode) {
						targetLangCode = nativeLangCode === 'en' ? 'fr' : 'en';
					}
					await ensureAnonymousSession(targetLangCode, nativeLangCode, true);
				} catch (e) {
					console.error(e);
				}
			}
		}
	}

	public static onCreate(
	): AppThunk {
		return async (
			dispatch,
			getState
		) => {
			const state = getState();
			const lang = getAuthUserLangCode(state);
			let multilingual = CallSelectors.getMultilingual(state);
			if (multilingual && !CallLangs.isLangMulti(lang)) {
				multilingual = false;
			}

			return ConferenceRest.createRoom(lang, multilingual)
				.then((res: TConferenceCreateRoomResult) => {

					const {
						name: roomId,
						token,
						saveLevel,
					} = res;

					let call: ICallState = {
						isActive: true,
						roomId,
						token,
						lang,
						multilingual,
						saveLevel,
						startedAt: toClientStartedAt(res.startedAt, res.serverNow),
					};

					dispatch(CallActions.update(call));

					Navigator.openCall(roomId);
					trackRoomCreated(roomId, lang, multilingual);

					return call;
				});
		}
	}

	public static onJoin(
		roomId: string,
	): AppThunk {
		return async (
			dispatch,
			getState
		) => {
			if (!getAuthUser(getState())) return;

			ConferenceRest.joinRoom(roomId)
				.then((res: TConferenceJoinResult) => CallAppEvents.applyJoinResult(roomId, res));
		}
	}

	// A call restored from localStorage (site reopened, no room in the URL) is checked before entering
	// Daily: a finished one is dropped and we stay on the main page instead of silently reopening it.
	// Resolves true when the call may be entered.
	public static onRestore(
		roomId: string,
	): AppThunk {
		return async (
			dispatch
		) => {
			let res: TConferenceJoinResult;
			try {
				res = await ConferenceRest.joinRoom(roomId);
			} catch (e) {
				return true; // can't tell — keep the old behaviour rather than drop a live call
			}
			if (res?.result && !res.finished) {
				CallAppEvents.applyJoinResult(roomId, res);
				return true;
			}
			dispatch(CallActions.update({
				isActive: false,
				roomId: '',
				token: '',
				startedAt: undefined,
			}));
			Navigator.openCall();
			return false;
		}
	}

	private static applyJoinResult(
		roomId: string,
		res: TConferenceJoinResult,
	) {
		const dispatch = getDispatch();
		let { isActive, roomId: room, token } = CallSelectors.getCall(getState());
		if (res.result) {
			// owner gets a token back on join
			const newToken = res.token || '';
			const startedAt = toClientStartedAt(res.startedAt, res.serverNow);
			if (!isActive || room !== roomId || (token || '') !== newToken) {
				dispatch(CallActions.update({
					isActive: true,
					roomId,
					token: newToken,
					notice: null,
					saveLevel: res.saveLevel,
					startedAt,
				}));
			} else {
				// startedAt isn't persisted, so a reload into the same room gets it only here
				dispatch(CallActions.update({ startedAt }));
			}
		} else if (res.reason === 'finished') {
			// Call is over and can't be re-joined: don't enter it, show the ended
			// message and drop the room from the URL.
			dispatch(CallActions.update({
				isActive: false,
				roomId: '',
				token: '',
				notice: 'finished',
				startedAt: undefined,
			}));
			Navigator.openCall();
		} else {
			// No such room: back to the home page with a note instead of the form.
			dispatch(CallActions.update({
				isActive: false,
				roomId: '',
				token: '',
				notice: 'notFound',
				startedAt: undefined,
			}));
			Navigator.openCall();
		}
	}

	public static onLeave(
	): AppThunk {
		return async (
			dispatch,
			getState
		) => {
			if (CallSelectors.getIsActive(getState())) {
				dispatch(CallActions.update({
					isActive: false,
					roomId: '',
					token: '',
					startedAt: undefined,
				}));
			}
		}
	}

	private static AUDIO_READY_INTERVAL = 2000;
	// Give up only after this long with NO server-side progress (updatedAt frozen), not by total
	// elapsed — long recordings can run far past any fixed cap while still making progress.
	private static AUDIO_READY_STALL_TIMEOUT = 15 * 60 * 1000;
	// How long a reprocess poll insists on seeing its own run before it accepts whatever the server
	// reports. Only a fallback: registration takes a couple of queries, so this should never be hit.
	private static AUDIO_READY_NEW_RUN_TIMEOUT = 30 * 1000;

	// saveLevel: the caller's own live value (from CallProvider context) — see conferenceRest.process
	// for why this closes a race against /settings' own async persist of the same field.
	public static async onProcess(
		saveLevel?: TConferenceSaveLevel,
	) {
		if (!CallSelectors.getToken(getState())) return; // send process by owner

		const dispatch = getDispatch();
		const roomId = CallSelectors.getRoomId(getState());

		// The "Unprocessed call" placeholder for this room, if the store has one (e.g. left over from
		// a mid-call page refresh's full groups fetch, before this /process call resolves).
		const isPlaceholderForRoom = (a: TAudio) => a.type === TAudioType.CONFERENCE && a.id < 0
			&& (a.roomName === roomId || a.audioS3FilePath?.includes(roomId));
		const findPlaceholder = () => {
			const group = getConferenceUserGroups(getState()).find(g => g.audios?.some(isPlaceholderForRoom));
			const audio = group?.audios?.find(isPlaceholderForRoom);
			return group && audio ? { group, audio } : null;
		};

		// Mark it busy for the round-trip so it doesn't sit static on "Unprocessed call" while
		// /process is in flight (saveLevel=nothing does its own cleanup before it even responds).
		const pending = findPlaceholder();
		if (pending) dispatch(updateAudioAction(pending.audio.id, pending.group.id, { finishingCall: true }));

		ConferenceRest.process(roomId, saveLevel)
			.then((res: TUserGroup | undefined) => {
				if (res?.id) {
					const group = getUserGroupById(getState(), res.id);
					const newAudio = res.audios?.reduce((max, a) => {
						return a.id > max.id ? a : max
					}, res.audios[0]);
					if (!newAudio.audioS3FilePath) {
						newAudio.audioS3FilePath = roomId;
					}
					if (group) {
						dispatch(refreshCallGroupAction(res));
					} else {
						dispatch(addGroup(res));
					}
					getConferenceUserGroups(getState())
						.filter(g => g.id < 0)
						.forEach(g => {
							dispatch(removeGroupAction(g.id))
						});
				} else if (!(res as any)?.error) {
					// saveLevel=nothing's success response is empty (no group to refresh from), even
					// though the server already finished its own cleanup. Drop the stale placeholder
					// ourselves, or it lingers until the next full reload replaces it.
					const stale = findPlaceholder();
					if (stale) {
						dispatch(removeAudioFromGroupAction(stale.audio.id, stale.group.id));
						if (!getUserGroupById(getState(), stale.group.id)?.audios?.length) {
							dispatch(removeGroupAction(stale.group.id));
						}
					}
				} else {
					// Genuine failure (e.g. not a participant, no owner) — leave the placeholder as
					// "Unprocessed call", just drop the loader so it's clear the request didn't stick.
					const stuck = findPlaceholder();
					if (stuck) dispatch(updateAudioAction(stuck.audio.id, stuck.group.id, { finishingCall: false }));
				}
			})
			.catch(() => {
				// Request failed outright (network error) rather than resolving with {error} — same
				// recovery as the error branch above.
				const stuck = findPlaceholder();
				if (stuck) dispatch(updateAudioAction(stuck.audio.id, stuck.group.id, { finishingCall: false }));
			})
			.finally(() => {
				dispatch(CallAppEffects.onEnd());
			});
	}

	public static pollAudioReadyByRoom(
		roomId: string,
		audioId?: number,
		stale?: { startedAt: number; updatedAt: number }, // pre-reprocess snapshot — skip until it moves.
		resume?: boolean, // re-attach to a live run: skip temp tile + seed.
		awaitNewRun?: boolean, // reprocess: don't call it done until the new run is actually registered.
	): () => void {
		const state = getState();
		const dispatch = getDispatch();
		// Stall detection: reset whenever the server advances updatedAt; time out only on inactivity.
		// Seed from the stale snapshot so a run that never moves still gives up after the stall window.
		let lastSeenUpdatedAt = stale?.updatedAt ?? 0;
		let lastActivityAt = Date.now();
		// A reprocess poll is started before /reprocess has registered its run server-side, so early
		// ticks still describe the PREVIOUS run — whose entry lingers ~60s and whose status is still
		// COMPLETED, i.e. ready/finished. Acting on that stops the poll on its very first tick.
		let runSeen = !awaitNewRun;
		const pollStartedAt = Date.now();
		// Proof the new run exists. A terminal snapshot never is: the previous run's updatedAt can still
		// be bumped after the client stopped polling it, so "differs from the stale stamp" proves
		// nothing — only a fresh startedAt, or a phase no finished run can be in, does.
		const isNewRun = (p?: TConferenceProgress) => !!p && (
			(p.phase !== 'DONE' && p.phase !== 'FAILED' && p.phase !== 'CANCELLED')
			|| (!!stale && p.startedAt > stale.startedAt)
		);
		// Requests already in flight when the poll ends must not write their older snapshot afterwards.
		let stopped = false;

		// Owner gets a real audio at once; a guest shows progress on the temp placeholder.
		const showProgressOnPlaceholder = !CallSelectors.getToken(state);

		let tempGroupId: number | null = null;
		let tempAudioId: number | null = null;
		let tempAudioGroupId: number | null = null;

		const langCode = getAuthUserLangCode(state);
		const targetGroup = getConferenceUserGroups(state).find(g =>
			g.languages.some(l => l.code === langCode)
		);

		// Match by id when known, else by roomId.
		const matchAudio = (a: TAudio) => audioId != null
			? a.id === audioId
			: !!a.audioS3FilePath?.includes(roomId);

		// On resume the real audio already exists — no placeholder needed.
		if (!resume) {
			const now = Date.now();
			const tempAudio: TAudio = { // fake audio while processing
				id: now,
				title: '',
				key: '',
				url: '',
				createdAt: new Date().toISOString(),
				language: undefined,
				savePhraseCount: 0,
				translatePhraseCount: 0,
				watchPercent: 0,
				type: TAudioType.AUDIO,
				position: 0,
				orderNum: 0,
				isLoading: true,
			};
			tempAudioId = tempAudio.id;

			if (targetGroup) {
				const audioExists = getConferenceUserGroups(state).some(g => g.audios.some(matchAudio));
				if (audioExists) {
					// audio added at ConferenceRest.process().then
				} else {
					tempAudioGroupId = targetGroup.id;
					dispatch(addAudioInGroup(tempAudio, targetGroup.id));
				}
			} else {
				const authUser = getAuthUser(state);
				const targetLang = getTargetLanguage(state);
				const languages: TUserLanguage[] = targetLang ? [targetLang] : [];
				const tempGroup: TUserGroup = { // fake group while processing
					id: -now - 1,
					name: '',
					ownerId: authUser?.id || 0,
					phrasesCount: 0,
					userPhrasesCount: 0,
					languages,
					audios: [tempAudio],
					description: '',
					public: false,
					level: EUserLevel.UNKNOWN,
					type: EUserGroupType.CONFERENCE,
				};
				tempGroupId = tempGroup.id;
				dispatch(addGroup(tempGroup));
			}
		}

		const cleanupTemp = () => {
			if (tempGroupId !== null) {
				dispatch(removeGroupAction(tempGroupId));
			} else if (tempAudioId !== null && tempAudioGroupId !== null) {
				dispatch(removeAudioFromGroupAction(tempAudioId, tempAudioGroupId));
			}
		};

		// False once the real audio appears and we drop the placeholder.
		let tempActive = tempAudioId !== null && (tempGroupId !== null || tempAudioGroupId !== null);
		let seeded = false;

		// The real audio for this room, once it exists in the store.
		const findRealTarget = (): {
			audioId: number;
			groupId: number;
			current?: TAudio;
		} | null => {
			const group = getConferenceUserGroups(getState()).find(g => g.audios.some(matchAudio));
			const audio = group?.audios.find(matchAudio);
			return group && audio ? {
				audioId: audio.id,
				groupId: group.id,
				current: audio,
			} : null;
		};

		// The temp placeholder — progress target for a guest until the poll finishes.
		const findTempTarget = (): {
			audioId: number;
			groupId: number;
			current?: TAudio;
		} | null => {
			const groupId = tempGroupId !== null ? tempGroupId : tempAudioGroupId;
			if (!tempActive || groupId === null || tempAudioId === null) return null;
			const group = getConferenceUserGroups(getState()).find(g => g.id === groupId);
			const current = group?.audios.find(a => a.id === tempAudioId);
			return current ? { audioId: tempAudioId, groupId, current } : null;
		};

		// Drive the modal from the real audio only (opens once): drop the temp tile and
		// seed the log on the real audio when it appears, to avoid flashing on the temp tile.
		const seedRealAndDropTemp = () => {
			const real = findRealTarget();
			if (!real) return;
			if (tempActive) {
				cleanupTemp();
				tempActive = false;
			}
			if (!seeded) {
				// On resume keep the snapshot — re-seeding "Starting…" would wipe progress and reset timers.
				if (!resume) {
					const progressLog = mergeProgressLog(real.current?.progressLog || [], 'Starting…');
					const now = Date.now();
					// Anchor the total timer now so it ticks from 0 before the first snapshot arrives.
					dispatch(updateAudioAction(real.audioId, real.groupId, { progress: undefined, progressLog, progressStartedAt: now, progressUpdatedAt: now, progressStepStartedAt: now }));
				}
				seeded = true;
			}
		};

		// Open now if the real audio exists (manual reprocess); else the poll loop seeds later.
		seedRealAndDropTemp();

		const storeProgress = (progress?: TConferenceProgress) => {
			if (!progress) return;
			// Real audio when it exists; for a guest fall back to the temp tile.
			const target = findRealTarget() || (showProgressOnPlaceholder ? findTempTarget() : null);
			if (!target) return;

			// accumulate the log in the store so it survives the tile remounting
			const patch = buildProgressPatch(
				target.current?.progressLog,
				progress,
				target.current?.progressStepStartedAt,
				target.current?.progressStartedAt,
			);

			dispatch(updateAudioAction(target.audioId, target.groupId, patch));
		};

		// Giving up on the poll must leave a TERMINAL log line: a log stuck on a non-terminal step keeps
		// the tile on "Processing…" (and hides Reprocess) until the page is reloaded.
		const failLog = (error: string) => {
			const target = findRealTarget() || (showProgressOnPlaceholder ? findTempTarget() : null);
			if (!target) return;
			dispatch(updateAudioAction(target.audioId, target.groupId, {
				progressLog: mergeProgressLog(target.current?.progressLog || [], `Failed: ${error}`),
				...(target.current?.progress ? { progress: { ...target.current.progress, phase: 'FAILED' as const, error } } : {}),
			}));
		};

		const intervalId = setInterval(async () => {
			if (Date.now() - lastActivityAt > CallAppEvents.AUDIO_READY_STALL_TIMEOUT) {
				stopped = true;
				clearInterval(intervalId);
				failLog('no progress from the server');
				cleanupTemp();
				return;
			}
			try {
				// once the real audio appears, drop the temp tile and seed the modal on it
				seedRealAndDropTemp();

				const result = await AudioRest.readyByRoom(roomId);
				// Ticks overlap: an earlier response may have ended the poll while this one was in
				// flight. Writing its (older, non-terminal) snapshot now would overwrite the terminal
				// log line and leave the tile frozen mid-phase with nothing left to poll it forward.
				if (stopped) return;
				const progress = result?.progress;

				// Skip the exact pre-reprocess snapshot until the backend moves it: a fresh run rotates
				// startedAt, a reused run bumps updatedAt. Matching startedAt alone would skip a reused
				// run's live snapshots forever (its startedAt never changes).
				if (stale && progress
					&& progress.startedAt === stale.startedAt
					&& progress.updatedAt === stale.updatedAt) {
					return;
				}

				// Until this reprocess' own run is visible, everything reported still describes the
				// previous one: neither log it nor let it end the poll.
				if (!runSeen) {
					if (isNewRun(progress) || Date.now() - pollStartedAt > CallAppEvents.AUDIO_READY_NEW_RUN_TIMEOUT) {
						runSeen = true;
					} else {
						return;
					}
				}

				// Server made progress → keep the poll alive; a frozen updatedAt lets the stall timer run.
				if (progress && progress.updatedAt > lastSeenUpdatedAt) {
					lastSeenUpdatedAt = progress.updatedAt;
					lastActivityAt = Date.now();
				}

				storeProgress(progress);
				// Terminal phases: stop here so the finished/ready branch below doesn't append "Done".
				if (progress?.phase === 'FAILED' || progress?.phase === 'CANCELLED') {
					stopped = true;
					clearInterval(intervalId);
					cleanupTemp();
					// Mirrors groupRestFormat.ts: only an audio with no url gets this flag touched, or a
					// failed reprocess of an already-built recording would wrongly hide Download/etc.
					const target = findRealTarget();
					if (target) {
						dispatch(updateAudioAction(target.audioId, target.groupId, { unprocessed: !target.current?.url }));
					}
					return;
				}
				if (result?.finished || result?.ready) {
					stopped = true;
					clearInterval(intervalId);
					cleanupTemp();
					if (result.group) {
						const existing = getUserGroupById(getState(), result.group.id);
						if (existing) {
							dispatch(refreshCallGroupAction(result.group));
						} else {
							dispatch(addGroup(result.group));
						}
					}
					if (!isUserGroupsLoaded(state)) {
						await dispatch(updateUserGroups(true));
					}
					// Backend reports finished/ready before progress reaches DONE. A live snapshot may
					// already be gone (evicted after its TTL) — synthesizing a partial TConferenceProgress
					// from it would render as "Failed: ... (NaN/undefined)" in getConferenceProgressMessage.
					if (progress) {
						storeProgress({ ...progress, phase: 'DONE' });
					} else {
						const target = findRealTarget() || (showProgressOnPlaceholder ? findTempTarget() : null);
						if (target) {
							dispatch(updateAudioAction(target.audioId, target.groupId, {
								progressLog: mergeProgressLog(target.current?.progressLog || [], 'Done'),
							}));
						}
					}
					// Run done: clear unprocessed (the refreshed group may omit it, leaving the stale true).
					const done = findRealTarget();
					if (done) {
						dispatch(updateAudioAction(done.audioId, done.groupId, { unprocessed: false }));
					}
				}
			} catch (e) {
				console.error(e);
			}
		}, CallAppEvents.AUDIO_READY_INTERVAL);

		return () => {
			stopped = true;
			clearInterval(intervalId);
			cleanupTemp();
		};
	}

	public static onLogin(): AppThunk {
		return async (
			dispatch,
			getState
		): Promise<boolean> => {
			EventsRouter.trackEvent(Events.SIGN_IN_CLICKED);
			const result = await AuthUserEffects.loginProcess({
				newUserCallback: AuthUserEffects.newUserDefaultHandler,
				targetLangName: '',
				nativeLangName: '',
				disableNavigateOnLogin: true
			});
			// No reload here: loginProcess -> prepareAfterOpen already resets the models of the
			// anonymous user and reloads languages/groups/settings for the real one, and CallApp
			// reacts to isAnonym flipping to false (start form, room join, Daily provider).
			return !!result && !!result.authUser;
		}
	}

	public static onChangeMultilingual(
		multilingual: boolean,
	): AppThunk {
		return (
			dispatch,
			getState
		) => {
			dispatch(CallActions.update({ multilingual }));
		}
	}

	public static checkLangs(
	): AppThunk {
		return (
			dispatch,
			getState
		) => {
			checkUserLanguages();
		}
	}

	// Dismiss the notice and show the new-call form again.
	public static onCloseNotice(
	): AppThunk {
		return (
			dispatch,
		) => {
			dispatch(CallActions.update({ notice: null }));
		}
	}

}
